import _extends from "@babel/runtime/helpers/esm/extends";
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { useResponsivePropValue } from '../../hooks';
import { resolveResponsiveProp } from '../../styles';
import { createDataTestId } from '../../utils';
import { zIndex as zIndexEnum } from '../constants/zIndex';
import { BoxBackgroundColor } from './Box.constants';
function resolveMarginStyles(props) {
  if (props.margin !== undefined) {
    return resolveResponsiveProp('margin', props.margin);
  }
  if (props.marginX !== undefined || props.marginY !== undefined) {
    const marginYStyles = props.marginY ? [resolveResponsiveProp('marginTop', props.marginY), resolveResponsiveProp('marginBottom', props.marginY)] : [];
    const marginXStyles = props.marginX ? [resolveResponsiveProp('marginLeft', props.marginX), resolveResponsiveProp('marginRight', props.marginX)] : [];
    return css(["", " ", ""], marginYStyles.length > 0 && css(["", " ", ""], marginYStyles[0], marginYStyles[1]), marginXStyles.length > 0 && css(["", " ", ""], marginXStyles[0], marginXStyles[1]));
  }
  return css(["", ";", ";", ";", ""], props.marginTop !== undefined && resolveResponsiveProp('marginTop', props.marginTop), props.marginRight !== undefined && resolveResponsiveProp('marginRight', props.marginRight), props.marginBottom !== undefined && resolveResponsiveProp('marginBottom', props.marginBottom), props.marginLeft !== undefined && resolveResponsiveProp('marginLeft', props.marginLeft));
}
function resolvePaddingStyles(props) {
  if (props.padding !== undefined) {
    return resolveResponsiveProp('padding', props.padding);
  }
  if (props.paddingX !== undefined || props.paddingY !== undefined) {
    const paddingYStyles = props.paddingY ? [resolveResponsiveProp('paddingTop', props.paddingY), resolveResponsiveProp('paddingBottom', props.paddingY)] : [];
    const paddingXStyles = props.paddingX ? [resolveResponsiveProp('paddingLeft', props.paddingX), resolveResponsiveProp('paddingRight', props.paddingX)] : [];
    return css(["", " ", ""], paddingYStyles.length > 0 && css(["", " ", ""], paddingYStyles[0], paddingYStyles[1]), paddingXStyles.length > 0 && css(["", " ", ""], paddingXStyles[0], paddingXStyles[1]));
  }
  return css(["", ";", ";", ";", ""], props.paddingTop !== undefined && resolveResponsiveProp('paddingTop', props.paddingTop), props.paddingRight !== undefined && resolveResponsiveProp('paddingRight', props.paddingRight), props.paddingBottom !== undefined && resolveResponsiveProp('paddingBottom', props.paddingBottom), props.paddingLeft !== undefined && resolveResponsiveProp('paddingLeft', props.paddingLeft));
}
function mapSingleTransition(item) {
  return Object.entries(item).map(_ref => {
    let [propertyName, timeValue] = _ref;
    return `${propertyName} ${timeValue}`;
  }).join(', ');
}
function resolveTransitionStyle(_ref2) {
  let {
    transitionables
  } = _ref2;
  if (transitionables === undefined) {
    return css([""]);
  }
  const transitionablesAsResponsiveValue = Array.isArray(transitionables) ? transitionables.map(item => mapSingleTransition(item)) : mapSingleTransition(transitionables);
  return resolveResponsiveProp('transition', transitionablesAsResponsiveValue);
}
const STYLING_PROPS = new Set(['display', 'width', 'height', 'overflow']);
function resolveBackgroundColorString(background, theme) {
  return Object.values(BoxBackgroundColor).includes(background) ? theme.Box.backgrounds[background] : background;
}
function resolveBackgroundColor(background, theme) {
  if (typeof background === 'string') {
    return resolveBackgroundColorString(background, theme);
  }
  if (Array.isArray(background)) {
    return background.map(item => resolveBackgroundColorString(item, theme));
  }
  return Object.fromEntries(Object.entries(background).map(_ref3 => {
    let [key, value] = _ref3;
    return [key, resolveBackgroundColorString(value, theme)];
  }));
}
const BoxRoot = styled.div.withConfig({
  shouldForwardProp(prop, defaultValidator) {
    return !STYLING_PROPS.has(prop) && defaultValidator(prop);
  }
}).withConfig({
  componentId: "ds__sc-1qsva2l-0"
})(["", ";", ";", " ", ";", ";", ";", ";", ";", ";", ";", ";", ";", ";", " ", " ", " ", ";", ";", ";", ";", ";", ";", " ", ";", " ", ";", " ", " ", " ", " ", " ", " [hidden]{display:none;}"], _ref4 => {
  let {
    display
  } = _ref4;
  return display && resolveResponsiveProp('display', display);
}, _ref5 => {
  let {
    flexDirection
  } = _ref5;
  return flexDirection && resolveResponsiveProp('flexDirection', flexDirection);
}, _ref6 => {
  let {
    flex
  } = _ref6;
  return flex !== undefined && resolveResponsiveProp('flex', flex);
}, _ref7 => {
  let {
    flexWrap
  } = _ref7;
  return flexWrap !== undefined && resolveResponsiveProp('flexWrap', flexWrap);
}, _ref8 => {
  let {
    flexGrow
  } = _ref8;
  return flexGrow !== undefined && resolveResponsiveProp('flexGrow', flexGrow);
}, _ref9 => {
  let {
    justifyContent
  } = _ref9;
  return justifyContent && resolveResponsiveProp('justifyContent', justifyContent);
}, _ref10 => {
  let {
    alignItems
  } = _ref10;
  return alignItems && resolveResponsiveProp('alignItems', alignItems);
}, _ref11 => {
  let {
    width
  } = _ref11;
  return width !== undefined && resolveResponsiveProp('width', width);
}, _ref12 => {
  let {
    minWidth
  } = _ref12;
  return minWidth !== undefined && resolveResponsiveProp('minWidth', minWidth);
}, _ref13 => {
  let {
    maxWidth
  } = _ref13;
  return maxWidth !== undefined && resolveResponsiveProp('maxWidth', maxWidth);
}, _ref14 => {
  let {
    height
  } = _ref14;
  return height !== undefined && resolveResponsiveProp('height', height);
}, _ref15 => {
  let {
    minHeight
  } = _ref15;
  return minHeight !== undefined && resolveResponsiveProp('minHeight', minHeight);
}, _ref16 => {
  let {
    maxHeight
  } = _ref16;
  return maxHeight !== undefined && resolveResponsiveProp('maxHeight', maxHeight);
}, _ref17 => {
  let {
    isFullViewportBackground,
    background,
    theme
  } = _ref17;
  return isFullViewportBackground && background !== undefined && css(["position:relative;z-index:", ";&::before{position:absolute;top:0;left:0;z-index:", ";width:100vw;height:100%;margin:0 calc(50% - 50vw);content:'';", "}"], zIndexEnum.DEFAULT, zIndexEnum.NEGATIVE_ONE, resolveResponsiveProp('background', resolveBackgroundColor(background, theme)));
}, resolveMarginStyles, resolvePaddingStyles, _ref18 => {
  let {
    position
  } = _ref18;
  return position !== undefined && resolveResponsiveProp('position', position);
}, _ref19 => {
  let {
    top
  } = _ref19;
  return top !== undefined && resolveResponsiveProp('top', top);
}, _ref20 => {
  let {
    right
  } = _ref20;
  return right !== undefined && resolveResponsiveProp('right', right);
}, _ref21 => {
  let {
    bottom
  } = _ref21;
  return bottom !== undefined && resolveResponsiveProp('bottom', bottom);
}, _ref22 => {
  let {
    left
  } = _ref22;
  return left !== undefined && resolveResponsiveProp('left', left);
}, _ref23 => {
  let {
    overflow
  } = _ref23;
  return overflow !== undefined && resolveResponsiveProp('overflow', overflow);
}, _ref24 => {
  let {
    zIndex
  } = _ref24;
  return zIndex !== undefined && css(["z-index:", ";"], zIndex);
}, _ref25 => {
  let {
    background,
    theme
  } = _ref25;
  if (typeof background === 'undefined') {
    return '';
  }
  return resolveResponsiveProp('background', resolveBackgroundColor(background, theme));
}, _ref26 => {
  let {
    borderRadius
  } = _ref26;
  return borderRadius !== undefined && resolveResponsiveProp('borderRadius', borderRadius);
}, _ref27 => {
  let {
    wordBreak
  } = _ref27;
  return wordBreak && resolveResponsiveProp('wordBreak', wordBreak);
}, resolveTransitionStyle, _ref28 => {
  let {
    gap
  } = _ref28;
  return gap !== undefined && resolveResponsiveProp('gap', gap);
}, _ref29 => {
  let {
    rowGap
  } = _ref29;
  return rowGap !== undefined && resolveResponsiveProp('rowGap', rowGap);
}, _ref30 => {
  let {
    columnGap
  } = _ref30;
  return columnGap !== undefined && resolveResponsiveProp('columnGap', columnGap);
}, _ref31 => {
  let {
    gridTemplateRows
  } = _ref31;
  return gridTemplateRows !== undefined && resolveResponsiveProp('gridTemplateRows', gridTemplateRows);
}, _ref32 => {
  let {
    gridTemplateColumns
  } = _ref32;
  return gridTemplateColumns !== undefined && resolveResponsiveProp('gridTemplateColumns', gridTemplateColumns);
});
export const Box = forwardRef(function Box(_ref33, ref) {
  let {
    isFullViewportBackground,
    testId,
    ...props
  } = _ref33;
  const isFullViewportBackgroundValue = useResponsivePropValue(isFullViewportBackground);
  return React.createElement(BoxRoot, _extends({
    isFullViewportBackground: isFullViewportBackgroundValue
  }, props, {
    ref: ref
  }, createDataTestId(testId), {
    "data-component": "Box"
  }));
});